import { Injectable } from "@angular/core";
import { ApiService } from "../../core/services/api.service";
import { SchedulingTypes } from "../../shared/constants";
import { SchedulingDTO } from "../../shared/DTO/SchedulingDTO";
import {
  Scheduling,
  Schedule,
  PowerLimitControl,
  EconomicTariffs,
  Cycle,
} from "./scheduling.model";
import { Observable } from "rxjs/Observable";
import { MacroGroupsDTO } from "../../shared/DTO/MacroGroupsDTO";
import { Device } from "../devices/device.model";
import { HttpParams } from "@angular/common/http";
import {
  RULE_SCHEDULE_TYPE,
  SCHEDULE_TYPE,
  RULE_TYPE,
} from "./scheduling.constants";
import { SessionStateService } from "../../core/services/session-state.service";
import { HOUSE_TARIFF, HOUSE_CYCLE } from "../houses/houses.constants";
import { cloneDeep } from "lodash";

@Injectable()
export class SchedulingService {
  economicTariffs: EconomicTariffs;

  constructor(
    private apiService: ApiService,
    private sessionStateService: SessionStateService
  ) {}

  getScheduling(
    houseId: string,
    moduleId?: string,
    schedulingType?: SCHEDULE_TYPE
  ): Observable<any> {
    let params;
    if (moduleId) {
      params = new HttpParams().set("moduleid", moduleId);
    }

    const rule = this.scheduleTypeToRule(schedulingType);

    return this.apiService
      .get(`/rule/house/${houseId}/rule${rule ? `/type/${rule}` : ""}`, {
        params: params,
      })
      .map((res) => Scheduling.fromRuleWrapperDTO(res));
  }

  createSchedule(houseId: string, schedule: Schedule): Observable<any> {
    const rule = this.scheduleTypeToRule(schedule.getType());
    return this.apiService.post(
      `/rule/house/${houseId}/rule${rule ? `/type/${rule}` : ""}`,
      Schedule.toRuleDefinitionDTO(schedule)
    );
  }

  deleteSchedule(houseId: string, schedule: Schedule): Observable<any> {
    return this.apiService.delete(
      `/rule/house/${houseId}/rule/id/${schedule.getId()}`
    );
  }

  updateSchedule(houseId: string, schedule: Schedule): Observable<any> {
    const rule = this.scheduleTypeToRule(schedule.getType());
    return this.apiService.put(
      `/rule/house/${houseId}/rule${
        rule ? `/type/${rule}` : ""
      }/${schedule.getId()}`,
      Schedule.toRuleDefinitionDTO(schedule)
    );
  }

  getPowerLimitControl(houseId: string): Observable<any> {
    return this.apiService
      .get(`/rule/house/${houseId}/power-limit-control`)
      .map((res) => PowerLimitControl.fromRuleWrapperDto(res));
  }

  updatePoweLimitControl(
    houseId: string,
    powerLimitControl: PowerLimitControl
  ): Observable<any> {
    return this.apiService.post(
      `/rule/house/${houseId}/power-limit-control`,
      PowerLimitControl.toRuleDefinitionDto(powerLimitControl)
    );
  }

  deconfigurePowerLimitControl(houseId: string) {
    return this.apiService.patch(
      `/rule/house/${houseId}/power-limit-control`,
      undefined
    );
  }

  updatePowerLimitControlEnabled(
    houseId: string,
    powerLimitControl: PowerLimitControl
  ) {
    return this.apiService.patch(
      `/rule/house/${houseId}/rule/id/${powerLimitControl.getId()}/state`,
      { state: powerLimitControl.getEnabled() }
    );
  }

  getEconomicTariffs() {
    return this.apiService
      .get("/interfacedata/tariffs/economictariffs")
      .map((res) => (this.economicTariffs = EconomicTariffs.fromDTO(res)));
  }

  getEconomicScheduling(schedule: Schedule) {
    let economicSchedules: Schedule[] = [];
    const tariff: HOUSE_TARIFF =
      this.sessionStateService.getHouseTariff().tariff;
    const cycle: HOUSE_CYCLE = this.sessionStateService.getHouseTariff().cycle;
    const cycleSchedule: Cycle = this.economicTariffs.getCycle(tariff, cycle);

    schedule
      .getWeekDays()
      .filter((weekday) => weekday.getSet())
      .forEach((element) => {
        const economicPeriod = cycleSchedule.getEconomicPeriod(
          cycle,
          element.getWeekDay()
        );
        economicSchedules = [
          ...economicSchedules,
          ...Schedule.createScheduleFromPeriod(
            schedule.getType(),
            schedule.getId(),
            cloneDeep(schedule.getWeekDays()),
            element,
            economicPeriod
          ),
        ];
      });
    return new Scheduling(economicSchedules);
  }

  private scheduleTypeToRule(scheduleType: SCHEDULE_TYPE): string {
    let rule;
    if (scheduleType === SCHEDULE_TYPE.ECONOMIC) {
      rule = this.buildScheduleEndoint(RULE_TYPE.LOW_COST_TARIFF);
    } else if (scheduleType === SCHEDULE_TYPE.STAND_BY_KILLER) {
      rule = this.buildScheduleEndoint(RULE_TYPE.STAND_BY_KILLER);
    } else if (
      scheduleType === null ||
      scheduleType === SCHEDULE_TYPE.HVAC ||
      scheduleType === SCHEDULE_TYPE.THERMOSTAT ||
      scheduleType === SCHEDULE_TYPE.ON_OFF
    ) {
      rule = this.buildScheduleEndoint(RULE_TYPE.SCHEDULE);
    }
    return rule;
  }

  private buildScheduleEndoint(pathname: string): string {
    return pathname
      .split(/(?=[A-Z])/)
      .join("-")
      .toLocaleLowerCase();
  }

  updateState(houseId: string, schedule: Schedule, state: boolean) {
    const body = {
      state: state,
    };
    return this.apiService.patch(
      `/rule/house/${houseId}/rule/id/${schedule.getId()}/state`,
      body
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/scheduling/scheduling.service.ts